import { Button } from "../../ui/button.tsx";
import { BadgeCheck, CheckCheck, Info, Loader2, Mail, Plus } from "lucide-react";
import { useUserDomain } from "../../../hooks/useUserDomain.ts";
import { useAddDomainToPostmark, useCheckEmailStatus, useAutoEmailStatusCheck } from "../../../hooks/usePostmarkDomain.ts";
import { Input } from "../../ui/input.tsx";
import { useState } from "react";
import Instructions from "./Instructions.tsx";
import DropDownMenu from "./DropDownMenu.tsx";
import InfoDialog from "./InfoDialog.tsx";
import RemoveEmailDialog from "./RemoveEmailDialog.tsx";
import DnsTable from "./DnsTable.tsx";

export function EmailsCard() {
  const [emailPrefix, setEmailPrefix] = useState('no-reply');
  const [showRemove, setShowRemove] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const { data: userDomain } = useUserDomain();
  const { mutate: addDomainToPostmark, isPending: isAddingDomain } = useAddDomainToPostmark();
  const { mutate: checkEmailStatus, isPending: isCheckingStatus } = useCheckEmailStatus();
  useAutoEmailStatusCheck(userDomain?.postmark_sender_id, userDomain?.email_status);

  return (
    <div className="flex flex-col border-t pt-6">
      <div className="flex items-center mb-2 font-semibold">
        {userDomain?.email_status === 'verified' ? <BadgeCheck className="size-5 mr-2 text-green-400" /> : <Info className="size-5 mr-2 text-yellow-400" />}
            Email Sending Setup
      </div>
      <p className="text-sm text-muted-foreground mb-10">Configure your domain to send emails directly from your own address.</p>
      <div className="flex items-center gap-2 mb-6">
        
        {userDomain?.email_status === 'not_setup' ? (
          <div className="flex flex-col ">  
            <p className="text-sm text-muted-foreground px-2 mb-4">
            To send emails from your own domain ( for example, no-reply@yourdomain.com ), you first need to create a mailbox in your hosting account.
            <br />
              We recommend creating a standard mailbox like no-reply@yourdomain.com.
            </p>
            <p className="text-sm text-muted-foreground mb-10 px-2">
            Provide your email address and we will then provide DNS records (SPF, DKIM, Return-Path) that must be added in your hosting panel. These records confirm that you own the domain and allow email services to trust your messages. Once the records are added and verified, you can send emails to your clients from your domain.
            </p>
            <div className="font-semibold mb-1">Your email address</div>
            <div className="flex items-center gap-0 border rounded self-start">
              <Input
                type="text"
                value={emailPrefix}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value.replace(/[^a-zA-Z0-9.-]/g, '');
                  setEmailPrefix(value);
                }}
                placeholder="no-reply"
                className="text-right  border-0 p-2 rounded-r-none mr-[1px] w-[200px]"
              />
              <span className=" bg-gray-200 p-2  text-sm font-semibold">@{userDomain?.domain}</span>
            </div>
            <Button 
              className="mt-4 self-start"
              onClick={() => addDomainToPostmark({ 
                domain: userDomain?.domain!, 
                emailPrefix: emailPrefix || 'no-reply'
              })}
              disabled={isAddingDomain || !emailPrefix.trim()}
            > 
              {isAddingDomain ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}Add Email
            </Button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <p className="text-xl font-semibold text-gray-900 mr-2">{userDomain?.email_from_address || `no-reply@${userDomain?.domain}`}</p>
                <div className={`flex  items-center px-3 py-1 rounded-full text-sm font-medium ${
                  userDomain?.email_status === 'pending' 
                    ? 'bg-gray-100 text-gray-700  '
                    : userDomain?.email_status === 'verified'
                    ? 'bg-green-100 text-green-700  '
                    : 'bg-red-100 text-red-700 '
                }  `}>
                  {userDomain?.email_status}
                </div>

              </div>
              <DropDownMenu
                dropdownOpen={dropdownOpen}
                setDropdownOpen={setDropdownOpen}
                isActive={userDomain?.email_status === 'verified'}
                setShowInfo={setShowInfo}
                setShowRemove={setShowRemove}
              />
            </div>
          </>
        )}
      </div>
      {userDomain?.email_status === 'pending' && 
        <>
          <div className="flex gap-3 items-center mb-2 mt-4">
            <Mail className="size-6 text-green-500" />
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">Confirm your email address</h3>
          </div>
          <p className="text-gray-700 dark:text-gray-300 mb-10 ">
          Postmark will send a confirmation email to complete domain verification. Please open the email and click the verification link to proceed. Then go to the next step to add the DNS records.
          </p>
          <Instructions title="Add these DNS records to your domain's DNS settings to verify ownership and enable email sending:"  isEmail={true} />
          <DnsTable items={userDomain?.postmark_dns_records?.map(record => ({ ...record, name: record.host })) || []} />
          <Button 
              className="mt-4 self-start" 
              onClick={() => checkEmailStatus(userDomain?.postmark_sender_id!)}
              disabled={isCheckingStatus}
            > 
              {isCheckingStatus ? <Loader2 className="size-4 animate-spin" /> : <CheckCheck className="size-4" />}Check Status
            </Button>
        </>
      }
      <InfoDialog 
        showInfo={showInfo} 
        setShowInfo={setShowInfo} 
        instructions={userDomain?.postmark_dns_records?.map(record => ({ ...record, name: record.host })) || []} 
      />
      <RemoveEmailDialog 
        showRemove={showRemove} 
        setShowRemove={setShowRemove} 
        userDomain={userDomain!} 
      />

    </div>
  )
}